/**
 * MapToolbar - Mobile
 * 
 * Single consolidated toolbar for all map controls.
 * Opens a clean panel with basemap, search, and tools.
 * Uses design tokens exclusively per Cursor Rules.
 */

import React, { useState, useCallback } from 'react';
import { 
  View, 
  Text, 
  TextInput,
  Pressable, 
  StyleSheet,
  Modal,
  ScrollView,
  ActivityIndicator,
  Keyboard,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { 
  color, 
  text, 
  space, 
  radius, 
  icon, 
  border, 
  shadow, 
  touch 
} from '../../../shared/tokens/mobile';
import { BasemapStyle } from '../../../hooks/useMapData';
import { logger } from '../../../shared/utils/logger';

// =============================================================================
// TYPES
// =============================================================================

interface BasemapOption {
  id: BasemapStyle;
  label: string;
  icon: keyof typeof Ionicons.glyphMap;
}

const BASEMAP_OPTIONS: BasemapOption[] = [
  { id: 'outdoors', label: 'Terrain', icon: 'trail-sign-outline' },
  { id: 'streets', label: 'Streets', icon: 'car-outline' },
  { id: 'satellite', label: 'Satellite', icon: 'earth-outline' },
  { id: 'satellite-streets', label: 'Hybrid', icon: 'globe-outline' },
  { id: 'light', label: 'Light', icon: 'sunny-outline' },
  { id: 'dark', label: 'Dark', icon: 'moon-outline' },
];

interface GeocodingResult {
  id: string;
  place_name: string;
  center: [number, number];
}

interface MapToolbarProps {
  mapboxToken: string | null;
  currentBasemap: BasemapStyle;
  onBasemapChange: (basemap: BasemapStyle) => void;
  onRefresh: () => void;
  onFitAll: () => void;
  onFlyTo: (lat: number, lng: number, zoom?: number, showLocation?: boolean) => void;
  hasOpportunities: boolean;
  refreshing?: boolean;
}

// =============================================================================
// COMPONENT
// =============================================================================

export function MapToolbar({
  mapboxToken,
  currentBasemap,
  onBasemapChange,
  onRefresh,
  onFitAll,
  onFlyTo,
  hasOpportunities,
  refreshing,
}: MapToolbarProps) {
  const [showPanel, setShowPanel] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<GeocodingResult[]>([]);
  const [locating, setLocating] = useState(false);

  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim() || !mapboxToken) return;
    
    Keyboard.dismiss();
    setSearching(true);
    setSearchResults([]);
    
    try {
      const encoded = encodeURIComponent(searchQuery.trim());
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encoded}.json?access_token=${mapboxToken}&country=us&limit=5`;
      
      const response = await fetch(url);
      if (!response.ok) throw new Error('Geocoding failed');
      
      const data = await response.json();
      const results: GeocodingResult[] = (data.features || [])
        .filter((f: any) => f.center && Array.isArray(f.center) && f.center.length >= 2)
        .map((f: any) => ({
          id: f.id,
          place_name: f.place_name,
          center: f.center as [number, number],
        }));
      
      if (results.length === 1 && results[0].center) {
        // Single result - fly directly and close
        onFlyTo(results[0].center[1], results[0].center[0], 14);
        setSearchQuery('');
        setShowPanel(false);
      } else {
        setSearchResults(results);
      }
    } catch (error) {
      logger.error('Geocoding error', 'map', String(error));
    } finally {
      setSearching(false);
    }
  }, [searchQuery, mapboxToken, onFlyTo]);

  const handleResultSelect = useCallback((result: GeocodingResult) => {
    if (result.center && result.center.length >= 2) {
      onFlyTo(result.center[1], result.center[0], 14);
    }
    setSearchQuery('');
    setSearchResults([]);
    setShowPanel(false);
  }, [onFlyTo]);

  const handleCurrentLocation = useCallback(async () => {
    if (!navigator?.geolocation) {
      logger.warn('Geolocation not supported', 'map');
      return;
    }
    
    setLocating(true);
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        onFlyTo(latitude, longitude, 14, true);
        setLocating(false);
        setShowPanel(false);
      },
      (error) => {
        logger.warn('Geolocation error', 'map', error.message);
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, [onFlyTo]);

  const handleRefresh = useCallback(() => {
    onRefresh();
    setShowPanel(false);
  }, [onRefresh]);

  const handleFitAll = useCallback(() => {
    onFitAll();
    setShowPanel(false);
  }, [onFitAll]);

  return (
    <>
      {/* Main toolbar button */}
      <View style={styles.container}>
        <Pressable
          onPress={() => setShowPanel(true)}
          style={({ pressed }) => [styles.button, pressed && styles.buttonPressed]}
          accessibilityLabel="Map tools"
          accessibilityRole="button"
        >
          <Ionicons name="options-outline" size={icon.md} color={color.textPrimary} />
        </Pressable>
      </View>

      {/* Tools panel modal */}
      <Modal
        visible={showPanel}
        animationType="slide"
        transparent
        onRequestClose={() => setShowPanel(false)}
      >
        <Pressable style={styles.backdrop} onPress={() => setShowPanel(false)} />
        
        <View style={styles.panel}>
          {/* Header */}
          <View style={styles.panelHeader}>
            <Text style={styles.panelTitle}>Map Tools</Text>
            <Pressable onPress={() => setShowPanel(false)} style={styles.closeButton}>
              <Ionicons name="close" size={icon.lg} color={color.textPrimary} />
            </Pressable>
          </View>

          <ScrollView 
            style={styles.panelScroll}
            contentContainerStyle={styles.panelContent}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
          >
            {/* Search Section */}
            <View style={styles.section}>
              <Text style={styles.sectionLabel}>SEARCH LOCATION</Text>
              <View style={styles.searchRow}>
                <TextInput
                  style={styles.searchInput}
                  placeholder="Address, city, or place..."
                  placeholderTextColor={color.textMuted}
                  value={searchQuery}
                  onChangeText={setSearchQuery}
                  onSubmitEditing={handleSearch}
                  returnKeyType="search"
                  autoCorrect={false}
                />
                <Pressable 
                  onPress={handleSearch}
                  style={({ pressed }) => [styles.searchButton, pressed && styles.searchButtonPressed]}
                  disabled={searching || !searchQuery.trim()}
                >
                  {searching ? (
                    <ActivityIndicator size="small" color={color.textInverse} />
                  ) : (
                    <Ionicons name="search" size={icon.sm} color={color.textInverse} />
                  )}
                </Pressable>
              </View>
              
              {/* Search Results */}
              {searchResults.length > 0 && (
                <View style={styles.searchResults}>
                  {searchResults.map((result) => (
                    <Pressable
                      key={result.id}
                      onPress={() => handleResultSelect(result)}
                      style={({ pressed }) => [
                        styles.searchResult,
                        pressed && styles.searchResultPressed,
                      ]}
                    >
                      <Ionicons name="location-outline" size={icon.sm} color={color.textMuted} />
                      <Text style={styles.searchResultText} numberOfLines={2}>
                        {result.place_name}
                      </Text>
                    </Pressable>
                  ))}
                </View>
              )}
            </View>

            {/* Quick Actions */}
            <View style={styles.section}>
              <Text style={styles.sectionLabel}>QUICK ACTIONS</Text>
              <View style={styles.actionsGrid}>
                <ToolButton
                  icon="locate"
                  label="My Location"
                  onPress={handleCurrentLocation}
                  loading={locating}
                />
                <ToolButton
                  icon="contract-outline"
                  label="Fit All"
                  onPress={handleFitAll}
                  disabled={!hasOpportunities}
                />
                <ToolButton
                  icon="refresh"
                  label="Refresh"
                  onPress={handleRefresh}
                  loading={refreshing}
                />
              </View>
            </View>

            {/* Basemap Section */}
            <View style={styles.section}>
              <Text style={styles.sectionLabel}>MAP STYLE</Text>
              <View style={styles.basemapGrid}>
                {BASEMAP_OPTIONS.map((option) => {
                  const isActive = currentBasemap === option.id;
                  return (
                    <Pressable
                      key={option.id}
                      onPress={() => onBasemapChange(option.id)}
                      style={[styles.basemapOption, isActive && styles.basemapOptionActive]}
                    >
                      <View style={[styles.basemapIcon, isActive && styles.basemapIconActive]}>
                        <Ionicons
                          name={option.icon}
                          size={icon.lg}
                          color={isActive ? color.primary : color.textSecondary}
                        />
                      </View>
                      <Text style={[styles.basemapLabel, isActive && styles.basemapLabelActive]}>
                        {option.label}
                      </Text>
                    </Pressable>
                  );
                })}
              </View>
            </View>
          </ScrollView>
        </View>
      </Modal>
    </>
  );
}

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

interface ToolButtonProps {
  icon: keyof typeof Ionicons.glyphMap;
  label: string;
  onPress: () => void;
  loading?: boolean;
  disabled?: boolean;
}

function ToolButton({ icon: iconName, label, onPress, loading, disabled }: ToolButtonProps) {
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled || loading}
      style={({ pressed }) => [
        styles.toolButton,
        pressed && !disabled && styles.toolButtonPressed,
        disabled && styles.toolButtonDisabled,
      ]}
    >
      <View style={styles.toolButtonIcon}>
        {loading ? (
          <ActivityIndicator size="small" color={color.primary} />
        ) : (
          <Ionicons 
            name={iconName} 
            size={icon.md} 
            color={disabled ? color.disabled : color.primary} 
          />
        )}
      </View>
      <Text style={[styles.toolButtonLabel, disabled && styles.toolButtonLabelDisabled]}>
        {label}
      </Text>
    </Pressable>
  );
}

// =============================================================================
// STYLES
// =============================================================================

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    top: space.sm,
    left: space.sm,
    zIndex: 20,
  },
  button: {
    width: touch.minimum,
    height: touch.minimum,
    borderRadius: radius.md,
    backgroundColor: color.surface,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: border.width.thin,
    borderColor: color.border,
    ...shadow.sm,
  },
  buttonPressed: {
    backgroundColor: color.pressed,
  },
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.3)',
  },
  panel: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    maxHeight: '70%',
    backgroundColor: color.surface,
    borderTopLeftRadius: radius.lg,
    borderTopRightRadius: radius.lg,
    ...shadow.sm,
  },
  panelHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: space.md,
    paddingVertical: space.md,
    borderBottomWidth: 1,
    borderBottomColor: color.border,
  },
  panelTitle: {
    fontSize: text.sectionTitle.fontSize,
    fontWeight: text.sectionTitle.fontWeight,
    color: color.textPrimary,
  },
  closeButton: {
    padding: space.xs,
  },
  panelScroll: {
    flex: 1,
  },
  panelContent: {
    padding: space.md,
    gap: space.lg,
    paddingBottom: space.xxl,
  },
  section: {
    gap: space.sm,
  },
  sectionLabel: {
    ...text.label,
    color: color.textMuted,
  },
  searchRow: {
    flexDirection: 'row',
    gap: space.sm,
  },
  searchInput: {
    flex: 1,
    height: touch.minimum,
    paddingHorizontal: space.md,
    backgroundColor: color.backgroundSecondary,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: color.border,
    fontSize: text.body.fontSize,
    color: color.textPrimary,
  },
  searchButton: {
    width: touch.minimum,
    height: touch.minimum,
    borderRadius: radius.md,
    backgroundColor: color.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  searchButtonPressed: {
    backgroundColor: color.primaryDark,
  },
  searchResults: {
    backgroundColor: color.backgroundSecondary,
    borderRadius: radius.md,
    overflow: 'hidden',
  },
  searchResult: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.sm,
    padding: space.sm,
    borderBottomWidth: 1,
    borderBottomColor: color.border,
  },
  searchResultPressed: {
    backgroundColor: color.pressed,
  },
  searchResultText: {
    flex: 1,
    fontSize: text.caption.fontSize,
    color: color.textPrimary,
  },
  actionsGrid: {
    flexDirection: 'row',
    gap: space.sm,
  },
  toolButton: {
    flex: 1,
    alignItems: 'center',
    gap: space.xs,
    paddingVertical: space.md,
    backgroundColor: color.backgroundSecondary,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: color.border,
  },
  toolButtonPressed: {
    backgroundColor: color.primaryLight,
    borderColor: color.primary,
  },
  toolButtonDisabled: {
    opacity: 0.5,
  },
  toolButtonIcon: {
    height: icon.lg,
    justifyContent: 'center',
  },
  toolButtonLabel: {
    fontSize: text.caption.fontSize,
    fontWeight: text.bodyMedium.fontWeight,
    color: color.textPrimary,
  },
  toolButtonLabelDisabled: {
    color: color.disabled,
  },
  basemapGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: space.sm,
  },
  basemapOption: {
    width: '23%',
    alignItems: 'center',
    gap: space.xs,
  },
  basemapOptionActive: {},
  basemapIcon: {
    width: 56,
    height: 56,
    borderRadius: radius.md,
    backgroundColor: color.backgroundSecondary,
    borderWidth: 2,
    borderColor: color.border,
    alignItems: 'center',
    justifyContent: 'center',
  },
  basemapIconActive: {
    backgroundColor: color.primaryLight,
    borderColor: color.primary,
  },
  basemapLabel: {
    fontSize: text.caption.fontSize,
    color: color.textSecondary,
  },
  basemapLabelActive: {
    color: color.primary,
    fontWeight: text.bodyMedium.fontWeight,
  },
});

